/**
 * Redux slice for recording state management
 * Handles microphone recording, WebSocket connection, and recording lifecycle
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// ===== TYPES =====

export type RecordingStatus = 'idle' | 'recording' | 'paused' | 'stopping' | 'stopped';
export type WebSocketStatus = 'disconnected' | 'connecting' | 'connected' | 'error';
export type TranscriptionStatus = 'idle' | 'transcribing' | 'paused' | 'stopped' | 'error';

interface RecordingState {
  // Recording state
  status: RecordingStatus;
  isRecording: boolean;
  isPaused: boolean;
  recordingStartTime: number | null;
  recordingDuration: number; // in seconds
  
  // Connection info
  connectionId: string | null;       // Client-generated connection ID
  activeTranscriptId: string | null;
  
  // WebSocket state
  wsStatus: WebSocketStatus;
  wsError: string | null;
  
  // Transcription state
  transcriptionStatus: TranscriptionStatus;
  transcriptionError: string | null;
  
  // Audio stream (not serializable, but tracked for cleanup)
  hasAudioStream: boolean;
  
  // Note recording state (mutually exclusive with conversation recording)
  isNoteRecording: boolean;
  isNoteRecordingDisabled: boolean;

  // File streaming state (audio file upload in progress)
  isFileStreaming: boolean;
  isFileStreamingPaused: boolean;
  fileStreamingProgress: number;
}

// ===== INITIAL STATE =====

const initialState: RecordingState = {
  status: 'idle',
  isRecording: false,
  isPaused: false,
  recordingStartTime: null,
  recordingDuration: 0,
  connectionId: null,
  activeTranscriptId: null,
  wsStatus: 'disconnected',
  wsError: null,
  transcriptionStatus: 'idle',
  transcriptionError: null,
  hasAudioStream: false,
  isNoteRecording: false,
  isNoteRecordingDisabled: false,
  isFileStreaming: false,
  isFileStreamingPaused: false,
  fileStreamingProgress: 0,
};

// ===== SLICE =====

const recordingSlice = createSlice({
  name: 'recording',
  initialState,
  reducers: {
    // Recording actions
    startRecording: (state, action: PayloadAction<{ connectionId: string }>) => {
      state.status = 'recording';
      state.isRecording = true;
      state.isPaused = false;
      state.recordingStartTime = Date.now();
      state.connectionId = action.payload.connectionId;
      state.activeTranscriptId = null;
    },
    pauseRecording: (state) => {
      state.status = 'paused';
      state.isPaused = true;
      state.isRecording = false;
    },
    resumeRecording: (state) => {
      state.status = 'recording';
      state.isPaused = false;
      state.isRecording = true;
    },
    stopRecording: (state) => {
      state.status = 'stopping';
    },
    recordingStopped: (state) => {
      state.status = 'stopped';
      state.isRecording = false;
      state.isPaused = false;
      state.activeTranscriptId = null;
    },
    resetRecording: (state) => {
      return initialState;
    },
    updateRecordingDuration: (state, action: PayloadAction<number>) => {
      state.recordingDuration = action.payload;
    },
    
    // WebSocket actions
    wsConnecting: (state) => {
      state.wsStatus = 'connecting';
      state.wsError = null;
    },
    wsConnected: (state) => {
      state.wsStatus = 'connected';
      state.wsError = null;
    },
    wsDisconnected: (state) => {
      state.wsStatus = 'disconnected';
    },
    wsError: (state, action: PayloadAction<string>) => {
      state.wsStatus = 'error';
      state.wsError = action.payload;
    },
    setActiveTranscriptId: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      state.activeTranscriptId = action.payload;
    },
    
    // Transcription actions
    transcriptionStarted: (state) => {
      state.transcriptionStatus = 'transcribing';
      state.transcriptionError = null;
    },
    transcriptionPaused: (state) => {
      state.transcriptionStatus = 'paused';
    },
    transcriptionResumed: (state) => {
      state.transcriptionStatus = 'transcribing';
    },
    transcriptionStopped: (state) => {
      state.transcriptionStatus = 'stopped';
    },
    transcriptionError: (state, action: PayloadAction<string>) => {
      state.transcriptionStatus = 'error';
      state.transcriptionError = action.payload;
    },
    
    // Audio stream tracking
    audioStreamAcquired: (state) => {
      state.hasAudioStream = true;
    },
    audioStreamReleased: (state) => {
      state.hasAudioStream = false;
    },
    
    // Note recording actions (mutually exclusive with conversation recording)
    startNoteRecording: (state) => {
      state.isNoteRecording = true;
    },
    stopNoteRecording: (state) => {
      state.isNoteRecording = false;
    },
    
    disableNoteRecording: (state) => {
      state.isNoteRecordingDisabled = true;
    },
    enableNoteRecording: (state) => {
      state.isNoteRecordingDisabled = false;
    },

    // File streaming actions
    startFileStreaming: (state) => {
      state.isFileStreaming = true;
      state.isFileStreamingPaused = false;
      state.fileStreamingProgress = 0;
      state.activeTranscriptId = null;
    },
    pauseFileStreaming: (state) => {
      state.isFileStreamingPaused = true;
    },
    resumeFileStreaming: (state) => {
      state.isFileStreamingPaused = false;
    },
    stopFileStreaming: (state) => {
      state.isFileStreaming = false;
      state.isFileStreamingPaused = false;
      state.fileStreamingProgress = 0;
      state.activeTranscriptId = null;
    },
    updateFileStreamingProgress: (state, action: PayloadAction<number>) => {
      state.fileStreamingProgress = action.payload;
    },
  },
});

export const {
  startRecording,
  pauseRecording,
  resumeRecording,
  stopRecording,
  recordingStopped,
  resetRecording,
  updateRecordingDuration,
  wsConnecting,
  wsConnected,
  wsDisconnected,
  wsError,
  setActiveTranscriptId,
  transcriptionStarted,
  transcriptionPaused,
  transcriptionResumed,
  transcriptionStopped,
  transcriptionError,
  audioStreamAcquired,
  audioStreamReleased,
  startNoteRecording,
  stopNoteRecording,
  enableNoteRecording,
  disableNoteRecording,
  startFileStreaming,
  pauseFileStreaming,
  resumeFileStreaming,
  stopFileStreaming,
  updateFileStreamingProgress
} = recordingSlice.actions;

export default recordingSlice.reducer;
